/**
 * Inline header search bar with dropdown predictive results.
 *
 * Renders the actual search input directly in the header (no modal trigger),
 * fetches the `predictive-search-drawer` section over AJAX while the user types,
 * and shows the results in a dropdown anchored below the input.
 *
 * Independent from the modal `action-search` element so it can coexist with the
 * legacy overlay search section without any wiring conflicts.
 */
(function () {
  if (customElements.get('header-inline-search')) return;

  var DEBOUNCE_MS = 250;
  var SECTION_ID = 'predictive-search-drawer';

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function getRoutes() {
    return (typeof window !== 'undefined' && window.routes) || {};
  }

  function getPredictiveUrl() {
    var r = getRoutes();
    return r.predictive_search_url || '/search/suggest';
  }

  function getSearchUrl() {
    var r = getRoutes();
    return r.search_url || '/search';
  }

  class HeaderInlineSearch extends HTMLElement {
    constructor() {
      super();
      this.input = null;
      this.form = null;
      this.dropdown = null;
      this.predictiveResults = null;
      this.clearBtn = null;
      this._abort = null;
      this._onInputDebounced = debounce(this._onInput.bind(this), DEBOUNCE_MS);
      this._onDocClick = this._onDocClick.bind(this);
      this._onDocKeydown = this._onDocKeydown.bind(this);
    }

    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.form = this.querySelector('form');
      this.dropdown = this.querySelector('[data-search-dropdown]');
      this.predictiveResults = this.querySelector('.predictive-search');
      this.clearBtn = this.querySelector('[data-search-clear]');

      if (!this.input || !this.form) return;

      this.input.addEventListener('input', this._onInputDebounced);
      this.input.addEventListener('focus', this._openDropdown.bind(this));

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', this._clear.bind(this));
      }

      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onDocKeydown);

      this._toggleClearBtn();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onDocKeydown);
      if (this._abort) this._abort.abort();
      if (this.input) {
        this.input.removeEventListener('input', this._onInputDebounced);
      }
      document.body.classList.remove('header-search-active');
    }

    _onDocClick(e) {
      if (!this.contains(e.target)) this._closeDropdown();
    }

    _onDocKeydown(e) {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this._clear();
        this._closeDropdown();
        if (this.input) this.input.blur();
      }
    }

    _openDropdown() {
      this.classList.add('is-open');
      this._syncBackdrop();
    }

    _closeDropdown() {
      this.classList.remove('is-open');
      document.body.classList.remove('header-search-active');
    }

    _syncBackdrop() {
      // Backdrop tracks dropdown visibility: only when the user has typed and
      // the results panel is actually displayed on screen.
      var visible =
        this.classList.contains('is-open') &&
        this.classList.contains('has-results');
      document.body.classList.toggle('header-search-active', visible);
    }

    _toggleClearBtn() {
      if (!this.clearBtn) return;
      var hasValue = this.input && this.input.value.trim().length > 0;
      this.clearBtn.classList.toggle('is-visible', hasValue);
    }

    _clear() {
      if (!this.input) return;
      this.input.value = '';
      if (this.predictiveResults) this.predictiveResults.innerHTML = '';
      this.classList.remove('has-results', 'is-loading', 'no-results');
      document.body.classList.remove('header-search-active');
      this._toggleClearBtn();
      this.input.focus();
    }

    _onInput() {
      this._toggleClearBtn();
      if (!this.input) return;
      var term = this.input.value.trim();
      if (!term.length) {
        if (this.predictiveResults) this.predictiveResults.innerHTML = '';
        this.classList.remove('has-results', 'is-loading', 'no-results');
        document.body.classList.remove('header-search-active');
        return;
      }

      this.classList.add('is-loading');
      this._openDropdown();

      var url =
        getPredictiveUrl() +
        '?q=' +
        encodeURIComponent(term) +
        '&section_id=' +
        SECTION_ID +
        '&resources[limit]=10&resources[limit_scope]=each' +
        // Explicit resource list: products and collections only. Search suggestion
        // chips ("Ajanlott termekek"), blog articles and pages are not wanted in
        // the header dropdown.
        '&resources[type]=product,collection';

      if (this._abort) this._abort.abort();
      this._abort = new AbortController();

      var self = this;
      fetch(url, { signal: this._abort.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (text) {
          var parsed = new DOMParser().parseFromString(text, 'text/html');
          var section = parsed.querySelector('#shopify-section-' + SECTION_ID);
          var html = section ? section.innerHTML : '';
          if (self.predictiveResults) self.predictiveResults.innerHTML = html;
          self.classList.remove('is-loading');
          self.classList.add('has-results');
          self._syncBackdrop();
          // Rewrite the "see more results" anchors to the full search results page for the current term.
          var moreLinks = self.querySelectorAll('.more-result-link');
          var searchUrl = getSearchUrl();
          moreLinks.forEach(function (a) {
            a.href =
              searchUrl +
              '?q=' +
              encodeURIComponent(term) +
              '&options%5Bprefix%5D=last';
          });
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          self.classList.remove('is-loading');
        });
    }
  }

  customElements.define('header-inline-search', HeaderInlineSearch);
})();
